import React, { useMemo, useState, useRef } from 'react';
import Border from '../common/border';
import ActivityTitle from '../common/ActivityTitle';
import ImageCarousel from '../common/ImageCarousel';
import InteractivePopover from '../common/InteractivePopover';
import CurrencyDisplay from '../common/CurrencyDisplay';
import { OptimizedImage } from '../common/OptimizedImage';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { parseRoomOptionsInput } from '@/utils/roomOptionHelpers';

export interface MediaItem {
  _id: string;
  filename?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  url: string;
  thumbnailUrl?: string | null;
  format: string;
  entityType?: string;
  entityId?: string;
  category?: string;
  tags?: string[];
  isActive?: boolean;
  isPublic?: boolean;
  uploadedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Hotel {
  address: string;
  name: string;
  type: string;
  location: string;
  roomTypes: string[];
  amenities: string[];
  imageUrl: string[];
  description: string;
  media?: MediaItem[];
  payAtSiteCost?: number;
  price?: number;
  pricePerNight?: number;
  currency?: string;
  currencySymbol?: string;
}

interface HotelsProps {
  hotel: Hotel[];
}

/**
 * Combines media and imageUrl arrays, prioritizing media items and excluding duplicates
 */
function getDeduplicatedImages(hotel: Hotel): string[] {
  // Extract URLs from media array (prioritize these)
  const mediaUrls = new Set<string>();
  if (hotel.media && Array.isArray(hotel.media)) {
    hotel.media
      .filter(item => item.format === 'image' && item.url)
      .forEach(item => mediaUrls.add(item.url));
  }

  // Combine media URLs with unique imageUrl items
  const allImages: string[] = [];

  // Add media URLs first (prioritized)
  mediaUrls.forEach(url => allImages.push(url));

  // Add imageUrl items that are NOT in media
  if (hotel.imageUrl && Array.isArray(hotel.imageUrl)) {
    hotel.imageUrl.forEach(url => {
      if (url && typeof url === 'string' && !mediaUrls.has(url)) {
        allImages.push(url);
      }
    });
  }

  return allImages;
}

const Hotels: React.FC<HotelsProps> = ({ hotel }) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<number, boolean>
  >({});
  const [shouldShowSeeMore, setShouldShowSeeMore] = useState<{
    [key: number]: boolean;
  }>({});
  const descriptionRefs = useRef<{
    [key: number]: HTMLParagraphElement | null;
  }>({});
  // Pre-compute deduplicated images for all hotels
  // Must be called before any early returns (React Hooks rule)
  const hotelsWithImages = useMemo(() => {
    if (!hotel || hotel.length === 0) {
      return [];
    }
    return hotel.map(detail => ({
      ...detail,
      images: getDeduplicatedImages(detail),
    }));
  }, [hotel]);

  // Check if descriptions actually overflow when clamped
  const checkDescriptionOverflow = (
    element: HTMLParagraphElement,
    key: number
  ) => {
    if (!element) return;

    // Create a temporary element to measure natural height
    const tempElement = element.cloneNode(true) as HTMLParagraphElement;
    tempElement.className = element.className
      .replace('line-clamp-3', '')
      .trim();
    tempElement.style.visibility = 'hidden';
    tempElement.style.position = 'absolute';
    tempElement.style.width = element.offsetWidth + 'px';
    tempElement.style.top = '-9999px';
    document.body.appendChild(tempElement);

    const naturalHeight = tempElement.scrollHeight;
    document.body.removeChild(tempElement);

    // Calculate expected height for 3 lines (line-height: 17px * 3 = 51px)
    // Add a small buffer for rounding
    const expectedClampedHeight = 17 * 3 + 2;

    // If natural height exceeds expected clamped height, show "see more"
    const hasOverflow = naturalHeight > expectedClampedHeight;

    setShouldShowSeeMore(prev => ({
      ...prev,
      [key]: hasOverflow,
    }));
  };
  if (!hotel || hotel.length === 0) {
    return null; // Don't render if there's no data
  }

  return (
    <div className='mt-8'>
      <ActivityTitle title='Accommodation' className='' />
      <div className='flex flex-wrap gap-4 pb-2 mb-8 pt-2 w-full'>
        {hotelsWithImages.map((hotelData, index) => {
          const hotelId = index;

          return (
            <div style={{ width: '135px' }} key={index}>
              <InteractivePopover
                triggerClassName='cursor-pointer'
                contentClassName='w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
                trigger={
                  <>
                    <div
                      onClick={e => e.stopPropagation()}
                      onMouseEnter={e => e.stopPropagation()}
                      onMouseLeave={e => e.stopPropagation()}
                    >
                      <div
                        style={{ width: '135px', height: '77px' }}
                        className='mb-2 overflow-hidden rounded-[8px]'
                      >
                        <ImageCarousel
                          images={hotelData.images}
                          alt={hotelData.name}
                          className='h-full w-full'
                          imageClassName='object-cover rounded-[8px]'
                          showNavigation={true}
                          navigationId={`hotel-${index}`}
                          buttonSize='sm'
                          loop={false}
                          enableFullscreen={true}
                          fullscreenTitle={hotelData.name}
                        />
                      </div>
                    </div>
                    <h4
                      className='text-[12px] font-normal text-black leading-[15px] mb-1 overflow-hidden'
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {hotelData.name}
                    </h4>
                    <div className='space-y-0.5'>
                      {hotelData.type && (
                        <p className='text-[10px] text-darkgrey capitalize'>
                          Type: {hotelData.type}
                        </p>
                      )}
                      {hotelData.location && (
                        <p className='text-[10px] text-darkgrey capitalize'>
                          {hotelData.location}
                        </p>
                      )}
                      {hotelData.address && (
                        <p className='text-[10px] text-darkgrey truncate'>
                          {hotelData.address}
                        </p>
                      )}
                    </div>
                  </>
                }
                content={
                  <div className='bg-white overflow-hidden flex flex-col max-h-[580px]'>
                    {/* Image Carousel */}
                    <div className='relative overflow-hidden'>
                      {hotelData.images.length > 0 ? (
                        <div className='w-full h-[180px] relative overflow-hidden'>
                          <Swiper
                            modules={[Pagination, A11y, Autoplay]}
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                            autoplay={{
                              delay: 3000,
                              disableOnInteraction: false,
                            }}
                            className='w-full h-full'
                          >
                            {hotelData.images.map((src, idx) => (
                              <SwiperSlide key={`${src}-${idx}`}>
                                <div className='relative w-full h-full'>
                                  <OptimizedImage
                                    src={src || '/assets/images/No-Image.jpg'}
                                    alt={`${hotelData.name} ${idx + 1}`}
                                    fill
                                    style={{ objectFit: 'cover' }}
                                    priority={idx === 0}
                                    loading={idx === 0 ? 'eager' : 'lazy'}
                                  />
                                </div>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>
                      ) : (
                        <div className='w-full h-[180px] relative overflow-hidden'>
                          <OptimizedImage
                            src='/assets/images/No-Image.jpg'
                            alt={hotelData.name}
                            fill
                            style={{ objectFit: 'cover' }}
                            priority
                            loading='eager'
                          />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className='p-3 overflow-y-auto flex-1'>
                      {/* Title */}
                      <h3 className='text-[14px] font-semibold text-black leading-[17px] mb-2 capitalize'>
                        {hotelData.name}
                      </h3>

                      {/* Type Tag */}
                      {hotelData.type && (
                        <div className='mb-2'>
                          <span className='inline-flex items-center gap-1.5 px-2 py-1 text-[12px] font-normal text-darkgrey leading-[15px] bg-gray-100 rounded border border-gray-200 capitalize'>
                            {hotelData.type}
                          </span>
                        </div>
                      )}

                      {(() => {
                        const nightlyPrice =
                          hotelData.payAtSiteCost ??
                          hotelData.pricePerNight ??
                          hotelData.price;

                        if (
                          nightlyPrice === undefined ||
                          nightlyPrice <= 0
                        ) {
                          return null;
                        }

                        return (
                          <div className='mb-2'>
                            <div className='flex items-baseline gap-0.5'>
                              <CurrencyDisplay
                                amount={nightlyPrice}
                                backendCurrency={hotelData.currency}
                                fontSize='14px'
                                className='font-semibold text-black'
                                showDecimals={false}
                              />
                              <span className='text-[12px] font-normal text-darkgrey'>
                                /night
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Description */}
                      {hotelData.description && (
                        <div className='mt-2'>
                          {(() => {
                            const isExpanded = expandedDescriptions[hotelId];
                            const shouldTruncate =
                              shouldShowSeeMore[hotelId] ?? false;

                            return (
                              <>
                                <p
                                  ref={el => {
                                    descriptionRefs.current[hotelId] = el;
                                    // Check overflow after element is rendered
                                    if (el) {
                                      // Use requestAnimationFrame to ensure DOM is updated
                                      requestAnimationFrame(() => {
                                        checkDescriptionOverflow(el, hotelId);
                                      });
                                    }
                                  }}
                                  className={`text-[12px] font-normal text-darkgrey leading-[17px] ${
                                    !isExpanded && shouldTruncate
                                      ? 'line-clamp-3'
                                      : ''
                                  }`}
                                >
                                  {hotelData.description}
                                </p>
                                {shouldTruncate && (
                                  <button
                                    onClick={() =>
                                      setExpandedDescriptions(prev => ({
                                        ...prev,
                                        [hotelId]: !prev[hotelId],
                                      }))
                                    }
                                    className='text-black font-medium mt-2 text-[11px] underline'
                                  >
                                    {isExpanded ? 'See less' : 'See more'}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* Room Types */}
                      {(() => {
                        const normalizedRoomTypes = parseRoomOptionsInput(
                          hotelData.roomTypes
                        );
                        if (normalizedRoomTypes.length === 0) return null;

                        return (
                          <div className='mt-3 pt-3 border-t border-gray-200'>
                            <p className='text-[12px] font-medium text-black mb-2'>
                              Room Types:
                            </p>
                            <div className='flex flex-wrap gap-2'>
                              {normalizedRoomTypes.map((roomType, idx) => (
                                <span
                                  key={roomType.id || idx}
                                  className='inline-flex items-center px-2 py-1 text-[11px] font-normal text-darkgrey bg-gray-100 rounded border border-gray-200'
                                >
                                  {roomType.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Amenities */}
                      {hotelData.amenities &&
                        hotelData.amenities.length > 0 && (
                          <div className='mt-3 pt-3 border-t border-gray-200'>
                            <p className='text-[12px] font-medium text-black mb-2'>
                              Amenities:
                            </p>
                            <div className='flex flex-wrap gap-2'>
                              {hotelData.amenities.map((amenity, idx) => (
                                <span
                                  key={idx}
                                  className='inline-flex items-center px-2 py-1 text-[11px] font-normal text-darkgrey bg-gray-100 rounded border border-gray-200'
                                >
                                  {amenity}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                }
                onOpenChange={() =>
                  setExpandedDescriptions(prev => ({
                    ...prev,
                    [hotelId]: false,
                  }))
                }
              />
            </div>
          );
        })}
      </div>
      <Border />
    </div>
  );
};

export default Hotels;
